
import { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { marked } from "marked";

import DOMPurify from "dompurify";
import SideBar from "../sidebar/SideBar";
import { formatDateRange } from '../utils/dateUtils';

import { BsFillArrowUpRightCircleFill } from 'react-icons/bs';
import firebaseApp from '../Firebase';
import { collection, getFirestore, query, getDocs, where } from "firebase/firestore";

const ProjectDate = ({ startDate, endDate, link }) => {
    const dateRange = formatDateRange(startDate, endDate);

    return (
        <div className='flex flex-wrap items-center justify-between gap-4 mb-8 pb-6 border-b border-slate-700/50'>
            <div className='flex items-center gap-3'>
                <div className='p-2 bg-slate-800/50 rounded-lg border border-slate-700/50'>
                    <svg className='w-5 h-5 text-slate-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' />
                    </svg>
                </div>
                <span className='text-slate-300 font-medium'>{dateRange}</span>
            </div>
            {link && (
                <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className='group flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-medium text-white shadow-lg hover:shadow-xl transition-all duration-300'
                >
                    <span>View Project</span>
                    <BsFillArrowUpRightCircleFill className='w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform' />
                </a>
            )}
        </div>
    );
};

ProjectDate.propTypes = {
    startDate: PropTypes.object.isRequired,
    endDate: PropTypes.object.isRequired,
    link: PropTypes.string.isRequired
};


const ProjectRenderer = ({ path }) => {
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const ref = getFirestore(firebaseApp);
        const q = query(collection(ref, "projects"), where("path", "==", path));
        getDocs(q)
            .then((querySnapshot) => {
                if (querySnapshot.docs.length > 0) {
                    setProject(querySnapshot.docs[0].data());
                } else {
                    setError("Project not found");
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching project:", err);
                setError("Failed to load project. Please try again later.");
                setLoading(false);
            });
    }, [path]);

    if (loading) {
        return (
            <>
                <SideBar />
            </>
        );
    }

    if (error) {
        return (
            <>
                <SideBar />
                <div className="min-h-screen flex items-center justify-center px-6 pb-20 md:pl-24 md:pb-0 md:pr-12 lg:pl-32 lg:pr-20">
                    <div className="glass-card p-8 max-w-md text-center">
                        <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h2 className="text-xl font-bold text-white mb-2">Oops!</h2>
                        <p className="text-red-400">{error}</p>
                    </div>
                </div>
            </>
        );
    }

    if (project) {
        marked.setOptions({
            stylesheet: "./ProjectRenderer.module.css"
        });
        return(
            <div className="min-h-screen">
                <SideBar />
                <div className="px-6 pb-24 md:pl-24 md:pb-0 md:pr-12 lg:pl-32 lg:pr-20 py-12 md:py-16 lg:py-20">
                    <div className="max-w-4xl mx-auto">
                        {/* Back Button */}
                        <a
                            href="/projects"
                            className="inline-flex items-center gap-2 text-slate-400 hover:text-emerald-400 mb-8 transition-colors duration-200"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            <span className="font-medium">Back to Projects</span>
                        </a>

                        {/* Title */}
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
                            <span className="gradient-text font-grotesk">{project.title}</span>
                        </h1>

                        {/* Date and Link */}
                        <ProjectDate startDate={project.start_date} endDate={project.end_date} link={project.link} />

                        {/* Content */}
                        <div>
                            <div
                                className="project-content"
                                dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(marked.parse(project.content))}}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

ProjectRenderer.propTypes = {
    path: PropTypes.string.isRequired
};

export default ProjectRenderer;
